import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, Transition as _Transition, toDisplayString as _toDisplayString, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"
import _imports_0 from 'assets/esp-kobudo-logo.png'


const _withScopeId = (n: any) => (_pushScopeId("data-v-7afe7144"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["id"]
const _hoisted_2 = {
  key: 0,
  class: "left-site-logo-style text-center"
}
const _hoisted_3 = { class: "bg-transparent" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("img", {
  src: _imports_0,
  alt: "Logo ESP Kobudo",
  title: "Logo ESP Kobudo"
}, null, -1 /* HOISTED */))
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = ["id"]
const _hoisted_8 = { class: "no-margin q-pb-sm" }
const _hoisted_9 = { class: "no-margin q-pb-sm" }
const _hoisted_10 = { class: "no-margin q-pb-sm" }
const _hoisted_11 = { class: "no-margin q-pb-sm" }

import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { QDrawer, useQuasar } from 'quasar';
import { useEventListener } from '@vueuse/core';

// Stores
import { useUiStore } from '@stores/ui';
import { useRouterStore } from '@stores/router';

// Personal components
import QMenuItem from '@layouts/components/QMenuItem.vue';
import QCopyrightComponent from '@pages/terms/components/QCopyrightComponent.vue';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// Personal libs
import { DRAWER_WIDTH_DESKTOP } from '@shared/conf/settings';
import { RouteEnum } from '@shared/enum/route.enum';
import { LocalStorageEnum } from '@shared/enum/localstorage.enum';
import bus from '@shared/lib/eventbus.lib';
import { BusEventEnum } from '@shared/enum/bus/bus-events.enum';
import { IProgram, IUser, IVideo } from '@shared/interface/model.interface';
import { RoleEnum } from '@shared/enum/role.enum';
import { UserStatusEnum } from '@shared/enum/user.enum';
import { VideoStatusEnum } from '@shared/enum/video-status.enum';
import { flatten } from '@shared/lib/tools.lib';
import { SkipLinkEnum } from '@shared/enum/skip-link.enum';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { EnvEnum } from '@shared/enum/env.enum';
import { logger } from '@shared/lib/logger/logger-manager';
import { menuList } from '@layouts/components/menus'
import { MenuCategoryEnum } from '@shared/enum/menu.enum';

/**
 * Globals
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'LayoutLeftSide',
  setup(__props) {

// Vendor
const $q = useQuasar();

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * Stores
 */
const uiStore = useUiStore();
const routerStore = useRouterStore();

/**
 * HTMLElement refs
 */
const leftPanelRef = ref<HTMLElement>();

/**
 * Computed properties
 */
const env = computed<EnvEnum>(() => process.env.ENV)
const connectedUser = computed<IUser | undefined>(
  () => sessionManager.currentUser.value,
);
const isAdmin = computed<boolean>(
  () => connectedUser.value?.roles?.includes(RoleEnum.ROLE_ADMIN) === true,
);
const isDeveloper = computed<boolean>(
  () => connectedUser.value?.roles?.includes(RoleEnum.ROLE_DEV) === true,
);
const isSuperAdmin = computed<boolean>(
  () =>
    connectedUser.value?.roles?.includes(RoleEnum.ROLE_SUPER_ADMIN) === true,
);
const isVideoValidator = computed<boolean>(
  () =>
    connectedUser.value?.roles?.includes(RoleEnum.ROLE_VIDEO_VALIDATE) === true,
);
const isUserValidator = computed<boolean>(
  () =>
    connectedUser.value?.roles?.includes(RoleEnum.ROLE_USER_VALIDATE) === true,
);
const drawerWidth = computed<number>(() => {
  const widthFromInnerWidth = (window.innerWidth / 3) * 2;
  if (widthFromInnerWidth > DRAWER_WIDTH_DESKTOP) {
    return DRAWER_WIDTH_DESKTOP;
  } else {
    return isMobile.value ? widthFromInnerWidth : DRAWER_WIDTH_DESKTOP;
  }
});
const activeRoute = computed<RouteEnum | undefined>({
  get: (): RouteEnum | undefined => routerStore.activeRoute,
  set: (value: RouteEnum | undefined) => routerStore.setActiveRoute(value),
});

const users = computed<IUser[]>(() => uiStore.users ?? []);
const nbUsersToValidate = computed<number | undefined>(() => {
  if (isUserValidator.value || isSuperAdmin.value) {
    return users.value.filter((u) => u.status === UserStatusEnum.VALIDATED)
      .length;
  } else {
    return undefined;
  }
});
const programs = computed<IProgram[]>(() => uiStore.programs ?? []);
const nbVideosToValidate = computed<number | undefined>(() => {
  if (isVideoValidator.value || isSuperAdmin.value) {
    return flatten<IVideo>(
      programs.value
        ?.filter((p) => p.videos != null && p.videos.length > 0)
        .map((p) => p.videos),
    ).filter((v) => v.status === VideoStatusEnum.TO_VALIDATE).length;
  } else {
    return undefined;
  }
});

/**
 * Reactive datas
 */
const miniState = ref<boolean | undefined>(true);
const left = ref<boolean | undefined>(!isMobile.value);

/**
 * Methods
 */
function toggleRightPanel() {
  bus.emit(BusEventEnum.RIGHT_PANEL_HIDE);
}

/**
 * Hooks
 */
// Hook : created
// Get local ministate
miniState.value = isMobile.value
  ? false
  : localStorage.getItem(LocalStorageEnum.LEFT_DRAWER_MINI_STATE) === 'true';

onMounted(async () => {
  // Set language (also set in the Home vue)
  await nextTick();

  useEventListener(leftPanelRef, 'click', toggleRightPanel);
  useEventListener(leftPanelRef, 'touchstart', toggleRightPanel);

  bus.on(BusEventEnum.LEFT_PANEL_TOGGLE, () => {
    if (!isMobile.value) {
      miniState.value = !miniState.value;
      localStorage.setItem(
        LocalStorageEnum.LEFT_DRAWER_MINI_STATE,
        miniState.value ? 'true' : 'false',
      );
    } else {
      left.value = !left.value;
    }
  });

  bus.on(BusEventEnum.LEFT_PANEL_HIDE, () => {
    left.value = false;
  });
});

onBeforeUnmount(() => {
  miniState.value = undefined;
  left.value = undefined;

  bus.off(BusEventEnum.LEFT_PANEL_HIDE);
  bus.off(BusEventEnum.LEFT_PANEL_TOGGLE);
});

return (_ctx: any,_cache: any) => {
  const _component_q_avatar = _resolveComponent("q-avatar")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_q_scroll_area = _resolveComponent("q-scroll-area")!

  return (_openBlock(), _createElementBlock("nav", {
    id: _unref(SkipLinkEnum).NAVIGATION_MENU,
    ref_key: "leftPanelRef",
    ref: leftPanelRef,
    role: "navigation",
    tabindex: "-1"
  }, [
    _createCommentVNode(" Left-side menu "),
    _createVNode(_unref(QDrawer), {
      modelValue: left.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((left).value = $event)),
      width: drawerWidth.value,
      "show-if-above": "",
      mini: miniState.value == true,
      side: "left",
      elevated: !_unref($q).dark.isActive,
      bordered: _unref($q).dark.isActive,
      behavior: _unref(isMobile) ? 'mobile' : 'desktop'
    }, {
      default: _withCtx(() => [
        _createVNode(_component_q_scroll_area, {
          class: "fit",
          "content-style": "display: flex; flex-wrap: wrap; flex-direction: column; justify-content: space-between;"
        }, {
          default: _withCtx(() => [
            _createCommentVNode(" Logo + Menu "),
            _createElementVNode("div", null, [
              _createCommentVNode(" Logo ESP Kobudo "),
              (_unref(isMobile))
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(_component_q_avatar, { size: "80px" }, {
                        default: _withCtx(() => [
                          _hoisted_4
                        ]),
                        _: 1 /* STABLE */
                      })
                    ]),
                    _createVNode(_component_q_separator, {
                      color: _unref($q).dark.isActive ? 'white' : 'black',
                      size: "2px",
                      inset: "",
                      class: "full-width"
                    }, null, 8 /* PROPS */, ["color"])
                  ]))
                : _createCommentVNode("v-if", true),
              _createCommentVNode(" Menu "),
              _createElementVNode("div", null, [
                (_openBlock(), _createBlock(_component_q_list, {
                  role: "list",
                  key: 'list0'
                }, {
                  default: _withCtx(() => [
                    _createCommentVNode(" Main access "),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(menuList).filter(m => m.category === _unref(MenuCategoryEnum).MAIN), (menuItem, index) => {
                      return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                        (
                    menuItem.authorizedRoles == null ||
                    menuItem.authorizedRoles.length == 0 ||
                    (menuItem.authorizedRoles.length !== 0 &&
                      menuItem.authorizedRoles.includes(_unref(RoleEnum).ROLE_READ) &&
                      connectedUser.value?.roles != null)
                  )
                          ? (_openBlock(), _createBlock(QMenuItem, {
                              key: 0,
                              "menu-item": menuItem
                            }, null, 8 /* PROPS */, ["menu-item"]))
                          : _createCommentVNode("v-if", true)
                      ], 64 /* STABLE_FRAGMENT */))
                    }), 128 /* KEYED_FRAGMENT */)),
                    _createVNode(_component_q_separator, {
                      class: _normalizeClass([
                  'separator',
                  { 'bg-white': _unref($q).dark.isActive, 'bg-black': !_unref($q).dark.isActive },
                ]),
                      "aria-hidden": "true"
                    }, null, 8 /* PROPS */, ["class"]),
                    _createCommentVNode(" Admin access "),
                    (isAdmin.value || isSuperAdmin.value)
                      ? (_openBlock(), _createBlock(_component_q_item_label, {
                          header: "",
                          class: "text-weight-bold text-uppercase",
                          key: 'item-label-header'
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" Administration ")
                          ]),
                          _: 1 /* STABLE */
                        }))
                      : _createCommentVNode("v-if", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(menuList).filter(m => m.category === _unref(MenuCategoryEnum).ADMIN), (menuItem, index) => {
                      return (_openBlock(), _createBlock(_Transition, {
                        key: index,
                        name: "fade",
                        mode: "out-in"
                      }, {
                        default: _withCtx(() => [
                          (
                      menuItem.authorizedRoles != null &&
                      menuItem.authorizedRoles.length > 0 &&
                      connectedUser.value?.roles != null &&
                      menuItem.authorizedRoles.some((r) =>
                        connectedUser.value?.roles?.includes(r),
                      )
                    )
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                _createVNode(QMenuItem, {
                                  "menu-item": menuItem,
                                  "nb-users-to-validate": nbUsersToValidate.value,
                                  "nb-videos-to-validate": nbVideosToValidate.value
                                }, null, 8 /* PROPS */, ["menu-item", "nb-users-to-validate", "nb-videos-to-validate"])
                              ]))
                            : _createCommentVNode("v-if", true)
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1024 /* DYNAMIC_SLOTS */))
                    }), 128 /* KEYED_FRAGMENT */)),
                    _createCommentVNode(" Developer access "),
                    (isDeveloper.value || env.value === _unref(EnvEnum).LOCAL)
                      ? (_openBlock(), _createBlock(_component_q_item_label, {
                          header: "",
                          class: "text-weight-bold text-uppercase",
                          key: 'item-label-header'
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(" Développeur ")
                          ]),
                          _: 1 /* STABLE */
                        }))
                      : _createCommentVNode("v-if", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(menuList).filter(m => m.category === _unref(MenuCategoryEnum).DEVELOPER), (menuItem, index) => {
                      return (_openBlock(), _createBlock(_Transition, {
                        key: index,
                        name: "fade",
                        mode: "out-in"
                      }, {
                        default: _withCtx(() => [
                          (
                      env.value === _unref(EnvEnum).LOCAL ||
                      menuItem.authorizedRoles != null &&
                      menuItem.authorizedRoles.length > 0 &&
                      connectedUser.value?.roles != null &&
                      (menuItem.authorizedRoles.some((r) =>
                        connectedUser.value?.roles?.includes(r),
                      ))
                    )
                            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                _createVNode(QMenuItem, { "menu-item": menuItem }, null, 8 /* PROPS */, ["menu-item"])
                              ]))
                            : _createCommentVNode("v-if", true)
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1024 /* DYNAMIC_SLOTS */))
                    }), 128 /* KEYED_FRAGMENT */))
                  ]),
                  _: 1 /* STABLE */
                }))
              ])
            ]),
            _createCommentVNode(" Footer "),
            (_unref(isMobile))
              ? (_openBlock(), _createElementBlock("footer", {
                  key: 0,
                  role: "contentinfo",
                  id: _unref(SkipLinkEnum).NAVIGATION_FOOTER,
                  class: _normalizeClass([
            'bg-transparent q-pl-sm q-pt-sm',
            {
              'text-white': _unref($q).dark.isActive,
              'text-black': !_unref($q).dark.isActive,
            },
          ])
                }, [
                  _createElementVNode("p", _hoisted_8, [
                    _createVNode(_component_router_link, {
                      class: _normalizeClass(
                _unref($q).dark.isActive ? 'router-link-white' : 'router-link-black'
              ),
                      to: `/${_unref(RouteEnum).SITE_MAP}`
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Plan du site " + _toDisplayString(activeRoute.value === _unref(RouteEnum).SITE_MAP ? '(actif)' : ''), 1 /* TEXT */)
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["class", "to"])
                  ]),
                  _createElementVNode("p", _hoisted_9, [
                    _createVNode(_component_router_link, {
                      class: _normalizeClass(
                _unref($q).dark.isActive ? 'router-link-white' : 'router-link-black'
              ),
                      to: `/${_unref(RouteEnum).MENTIONS_LEGALES}`
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Mentions légales " + _toDisplayString(activeRoute.value === _unref(RouteEnum).MENTIONS_LEGALES ? '(actif)' : ''), 1 /* TEXT */)
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["class", "to"])
                  ]),
                  _createElementVNode("p", _hoisted_10, [
                    _createVNode(_component_router_link, {
                      class: _normalizeClass(
                _unref($q).dark.isActive ? 'router-link-white' : 'router-link-black'
              ),
                      to: `/${_unref(RouteEnum).CGU}`
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Conditions générales " + _toDisplayString(activeRoute.value === _unref(RouteEnum).CGU ? '(actif)' : ''), 1 /* TEXT */)
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["class", "to"])
                  ]),
                  _createElementVNode("p", _hoisted_11, [
                    _createVNode(_component_router_link, {
                      class: _normalizeClass(
                _unref($q).dark.isActive ? 'router-link-white' : 'router-link-black'
              ),
                      to: `/${_unref(RouteEnum).A11Y}`
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Accessibilité : partiellement conforme " + _toDisplayString(activeRoute.value === _unref(RouteEnum).A11Y ? '(actif)' : ''), 1 /* TEXT */)
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["class", "to"])
                  ]),
                  _createVNode(QCopyrightComponent)
                ], 10 /* CLASS, PROPS */, _hoisted_7))
              : _createCommentVNode("v-if", true)
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["modelValue", "width", "mini", "elevated", "bordered", "behavior"])
  ], 8 /* PROPS */, _hoisted_1))
}
}

})